import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'
import { Dropdown } from 'primereact/dropdown'
import { InputSwitch } from 'primereact/inputswitch'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'
import { FC, useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import { RaceTeam, User } from '../../types/types'
import { currentUserState } from '../../utils/state-atoms'
import { DropdownStyle } from '../../utils/styling-constants'
import { PlusIcon } from '../Icons'
import Modal from '../Modal'

//temporary user roles list until API or Enum or Utility Const is created
const USER_ROLES = [
  { label: 'Aero Engineer/Standard User', value: 'aero-engineer' },
  { label: 'Methodology Specialist', value: 'methodology-specialist' },
  { label: 'Developer', value: 'developer' },
  { label: 'Admin', value: 'admin' },
  { label: 'Read-Only', value: 'read-only' },
  { label: 'Resubmit-Only', value: 'resubmit-only' },
  { label: 'Dashboard-Only', value: 'dashboard-only' }
]

// Map legacy role IDs to current valid values
const LEGACY_ROLE_MAPPING: Record<string, string> = {
  engineer: 'aero-engineer',
  Engineer: 'aero-engineer',
  standard: 'aero-engineer',
  Standard: 'aero-engineer'
}

const normalizeRoleId = (roleId: string): string => {
  return LEGACY_ROLE_MAPPING[roleId] || roleId
}

type TeamAssignment = {
  recordId?: string
  raceTeamId: string
  role: string
  rescaleToken?: string
  // True when a token is stored server-side; its value is redacted on read.
  hasToken?: boolean
  isNew?: boolean
}

type RemovedTeamAssignment = {
  recordId: string
  raceTeamId: string
}

type ModifiedTeamAssignment = {
  recordId: string
  raceTeamId: string
  role: string
  rescaleToken?: string
}

type UserEditForm = {
  firstName: string
  lastName: string
  email: string
}

type UserEditModalProps = {
  visible: boolean
  user: User | null
  raceTeams: RaceTeam[]
  onHide: () => void
  onSave: (
    updatedUser: User,
    newTeamAssignments?: TeamAssignment[],
    removedAssignments?: RemovedTeamAssignment[],
    modifiedAssignments?: ModifiedTeamAssignment[]
  ) => void
}

const UserEditModal: FC<UserEditModalProps> = ({ visible, user, raceTeams, onHide, onSave }) => {
  const currentUser = useRecoilValue(currentUserState)
  const isAdmin = currentUser?.role?.roleId === 'admin'

  const [editingEnabled, setEditingEnabled] = useState(false)
  const [teamAssignments, setTeamAssignments] = useState<TeamAssignment[]>([])
  const [showAddTeam, setShowAddTeam] = useState(false)
  const addTeamFormRef = useRef<HTMLDivElement>(null)
  const [newTeamForm, setNewTeamForm] = useState({ raceTeamId: '', role: '', rescaleToken: '' })
  const [hasChanges, setHasChanges] = useState(false)
  const [removedAssignments, setRemovedAssignments] = useState<RemovedTeamAssignment[]>([])
  const [roleChanged, setRoleChanged] = useState(false)
  const [tokenChanged, setTokenChanged] = useState(false)
  const [originalTeamAssignments, setOriginalTeamAssignments] = useState<TeamAssignment[]>([])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty }
  } = useForm<UserEditForm>()

  // Scroll to Add Team form when it appears
  useEffect(() => {
    if (showAddTeam && addTeamFormRef.current) {
      addTeamFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [showAddTeam])

  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || ''
      })

      // Initialize team assignments from all user records
      const rawRecords = ((user as any).allTeamRecords || [user]) as User[]
      const sortedRecords = [...rawRecords].sort((a, b) => {
        if (user._id && a._id === user._id) return -1
        if (user._id && b._id === user._id) return 1
        return 0
      })
      const assignments: TeamAssignment[] = sortedRecords.map((record: User) => ({
        recordId: (record as any)?._id || record.userId,
        raceTeamId: record.raceTeamId || '',
        role: normalizeRoleId(record.role?.roleId || ''),
        rescaleToken: record.rescaleTokens?.[0]?.token || '',
        hasToken: (record.rescaleTokens?.length ?? 0) > 0
      }))
      setTeamAssignments(assignments)
      setOriginalTeamAssignments(JSON.parse(JSON.stringify(assignments)))

      setEditingEnabled(false)
      setShowAddTeam(false)
      setHasChanges(false)
      setRemovedAssignments([])
      setRoleChanged(false)
      setTokenChanged(false)
    }
  }, [user, reset])

  useEffect(() => {
    setHasChanges(
      isDirty ||
        teamAssignments.some(a => a.isNew) ||
        removedAssignments.length > 0 ||
        roleChanged ||
        tokenChanged
    )
  }, [isDirty, teamAssignments, removedAssignments, roleChanged, tokenChanged])

  useEffect(() => {
    if (!editingEnabled) {
      setShowAddTeam(false)
    }
  }, [editingEnabled])

  const ensureEditingEnabled = () => {
    if (!editingEnabled) {
      toast.error('Enable editing before making changes')
      return false
    }
    return true
  }

  const raceTeamOptions = raceTeams.map(team => ({
    label: team.raceTeamId || team.name || team._id,
    value: team.raceTeamId || team._id
  }))

  const handleAddTeam = () => {
    if (!ensureEditingEnabled()) return

    if (!newTeamForm.raceTeamId || !newTeamForm.role) {
      toast.error('Race team and role are required')
      return
    }

    const exists = teamAssignments.find(
      a => a.raceTeamId === newTeamForm.raceTeamId && a.role === newTeamForm.role
    )
    if (exists) {
      toast.error('This team/role combination already exists')
      return
    }

    setTeamAssignments([...teamAssignments, { ...newTeamForm, isNew: true }])
    setNewTeamForm({ raceTeamId: '', role: '', rescaleToken: '' })
    setShowAddTeam(false)
    setHasChanges(true)
  }

  const handleRemoveTeam = (index: number) => {
    if (!ensureEditingEnabled()) return

    const assignmentToRemove = teamAssignments[index]
    const isLastTeam = teamAssignments.length === 1

    confirmDialog({
      message: isLastTeam
        ? "This is the user's only team assignment. Removing it will delete the user entirely. Are you sure?"
        : 'Are you sure you want to remove this user from the team?',
      header: isLastTeam ? 'Confirm User Deletion' : 'Confirm Team Removal',
      icon: 'pi pi-exclamation-triangle text-red-500',
      rejectClassName: 'btn bg-gray-500 text-white',
      defaultFocus: 'reject',
      accept: () => {
        setTeamAssignments(teamAssignments.filter((_, i) => i !== index))
        if (assignmentToRemove?.recordId) {
          setRemovedAssignments(prev => [
            ...prev,
            { recordId: assignmentToRemove.recordId, raceTeamId: assignmentToRemove.raceTeamId }
          ])
        }
        setHasChanges(true)
      }
    })
  }

  const handleUpdateTeamToken = (index: number, token: string) => {
    if (!ensureEditingEnabled()) return

    const newAssignments = [...teamAssignments]
    newAssignments[index] = {
      ...newAssignments[index],
      rescaleToken: token
    }
    setTeamAssignments(newAssignments)
    setTokenChanged(true)
  }

  const handleSave = (formData: UserEditForm) => {
    if (!user) return
    if (!editingEnabled) {
      toast.error('Enable editing before saving changes')
      return
    }

    // Trim whitespace from user input
    const trimmedData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim()
    }

    // Validate trimmed data is not empty
    if (!trimmedData.firstName) {
      toast.error('First name cannot be empty')
      return
    }
    if (!trimmedData.lastName) {
      toast.error('Last name cannot be empty')
      return
    }
    if (!trimmedData.email) {
      toast.error('Email cannot be empty')
      return
    }

    // If all team assignments have been removed, this is a full user deletion
    if (teamAssignments.length === 0 && removedAssignments.length > 0) {
      onSave(
        {
          ...user,
          firstName: trimmedData.firstName,
          lastName: trimmedData.lastName,
          email: trimmedData.email
        } as User,
        undefined,
        removedAssignments,
        undefined
      )
      return
    }

    // Build updated user object for the primary team assignment
    // Use the role from teamAssignments which may have been edited
    const primaryRole = teamAssignments[0]?.role || normalizeRoleId(user.role?.roleId || '')
    const roleName = USER_ROLES.find(r => r.value === primaryRole)?.label || primaryRole

    // Token values arrive redacted (empty), so only override rescaleTokens when
    // the admin actually changed the primary token. Otherwise leave the existing
    // (redacted) tokens in place so the server preserves the stored credential.
    const originalPrimaryToken = originalTeamAssignments[0]?.rescaleToken || ''
    const currentPrimaryToken = teamAssignments[0]?.rescaleToken || ''
    const primaryTokenChanged = currentPrimaryToken !== originalPrimaryToken

    const updatedUser = {
      ...user,
      firstName: trimmedData.firstName,
      lastName: trimmedData.lastName,
      email: trimmedData.email,
      raceTeamId: teamAssignments[0]?.raceTeamId || user.raceTeamId,
      role: {
        roleId: primaryRole,
        name: roleName
      },
      ...(primaryTokenChanged
        ? { rescaleTokens: currentPrimaryToken ? [{ token: currentPrimaryToken }] : [] }
        : {})
    } as User

    // Extract new team assignments (those marked with isNew: true)
    const newTeamAssignments = teamAssignments.slice(1).filter(ta => ta.isNew)

    // Find modified existing team assignments (non-primary, non-new, but changed)
    const modifiedAssignments: ModifiedTeamAssignment[] = []
    teamAssignments.slice(1).forEach((assignment, idx) => {
      if (!assignment.isNew && assignment.recordId) {
        const originalIdx = originalTeamAssignments.findIndex(
          a => a.recordId === assignment.recordId
        )
        if (originalIdx >= 0) {
          const original = originalTeamAssignments[originalIdx]
          // Check if token changed
          if ((assignment.rescaleToken || '') !== (original.rescaleToken || '')) {
            modifiedAssignments.push({
              recordId: assignment.recordId,
              raceTeamId: assignment.raceTeamId,
              role: assignment.role,
              rescaleToken: assignment.rescaleToken
            })
          }
        }
      }
    })

    onSave(
      updatedUser,
      newTeamAssignments.length > 0 ? newTeamAssignments : undefined,
      removedAssignments.length > 0 ? removedAssignments : undefined,
      modifiedAssignments.length > 0 ? modifiedAssignments : undefined
    )
  }

  const handleCancel = () => {
    if (hasChanges) {
      confirmDialog({
        message: 'Discard unsaved changes?',
        header: 'Confirm Cancel',
        icon: 'pi pi-exclamation-triangle',
        defaultFocus: 'reject',
        accept: onHide
      })
    } else {
      onHide()
    }
  }

  if (!user) return null

  return (
    <Modal
      isOpen={visible}
      withAction={false}
      easyClose={!hasChanges}
      onCancel={handleCancel}
      title={`${user.firstName} ${user.lastName}`}
      size="lg"
      zIndex={30}>
      <div className="flex flex-col max-h-[70vh]">
        {/* Content - scrollable area with explicit max height */}
        <div className="flex-1 overflow-y-auto min-h-0 pr-2">
          <form onSubmit={handleSubmit(handleSave)} className="space-y-6 -mt-4">
            {/* Sticky header with edit toggle */}
            {isAdmin && (
              <div className="sticky top-0 z-10 !h-0 pointer-events-none">
                <div className="ml-auto flex items-center gap-2 w-fit bg-white dark:bg-neutral-90 max-w-fit pointer-events-auto">
                  <label className="text-sm font-medium text-neutral-80 dark:text-white">
                    Enable editing
                  </label>
                  <InputSwitch
                    checked={editingEnabled}
                    onChange={e => setEditingEnabled(e.value)}
                  />
                </div>
              </div>
            )}

            {/* Identity Section */}
            <div className="space-y-4 relative">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gm-blue dark:text-light-blue">
                  Identity
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-semibold text-neutral-80 dark:text-white">
                    First Name {editingEnabled && <span className="text-red-500">*</span>}
                  </label>
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{ required: editingEnabled && 'First name is required' }}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        disabled={!editingEnabled}
                        className={classNames({ 'p-invalid': errors.firstName })}
                      />
                    )}
                  />
                  {errors.firstName && (
                    <small className="text-red-500 mt-1">{errors.firstName.message}</small>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-semibold text-neutral-80 dark:text-white">
                    Last Name {editingEnabled && <span className="text-red-500">*</span>}
                  </label>
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{ required: editingEnabled && 'Last name is required' }}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        disabled={!editingEnabled}
                        className={classNames({ 'p-invalid': errors.lastName })}
                      />
                    )}
                  />
                  {errors.lastName && (
                    <small className="text-red-500 mt-1">{errors.lastName.message}</small>
                  )}
                </div>
              </div>

              <div className="flex flex-col">
                <label className="mb-2 text-sm font-semibold text-neutral-80 dark:text-white">
                  Email {editingEnabled && <span className="text-red-500">*</span>}
                </label>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: editingEnabled && 'Email is required',
                    pattern: editingEnabled && {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  }}
                  render={({ field }) => (
                    <InputText
                      {...field}
                      disabled={true}
                      className={classNames({ 'p-invalid': errors.email })}
                      title="Email cannot be changed after user creation"
                    />
                  )}
                />
                {errors.email && (
                  <small className="text-red-500 mt-1">{errors.email.message}</small>
                )}
                <small className="text-neutral-60 dark:text-neutral-10 mt-1">
                  Email address cannot be changed after creation
                </small>
              </div>
            </div>

            {/* Race Team Assignments */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gm-blue dark:text-light-blue">
                  Race Team Assignments
                </h3>
                {editingEnabled && (
                  <Button
                    type="button"
                    icon={<PlusIcon className="fill-white" />}
                    label="Add Team"
                    onClick={() => setShowAddTeam(true)}
                    disabled={!editingEnabled}
                    className="p-button-sm p-button-outlined"
                  />
                )}
              </div>

              <div className="space-y-3">
                {teamAssignments.map((assignment, index) => {
                  const team = raceTeams.find(t => t.raceTeamId === assignment.raceTeamId)
                  const role = USER_ROLES.find(r => r.value === assignment.role)
                  const isPrimary = index === 0 && !assignment.isNew
                  return (
                    <div
                      key={index}
                      className="p-4 border border-neutral-50 dark:border-neutral-70 rounded bg-neutral-5 dark:bg-neutral-85">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-3">
                          <div className="flex items-start gap-2 w-full">
                            <div className="basis-0 grow">
                              <div className="font-semibold text-gm-blue dark:text-light-blue">
                                {team?.raceTeamId || team?.name || assignment.raceTeamId}
                                {isPrimary && (
                                  <span className="ml-2 text-xs font-normal text-neutral-60">
                                    (Primary)
                                  </span>
                                )}
                              </div>
                              {/* Show dropdown for primary assignment when editing, otherwise show text */}
                              {isPrimary && editingEnabled ? (
                                <div className="mt-2">
                                  <Dropdown
                                    value={assignment.role}
                                    options={USER_ROLES}
                                    onChange={e => {
                                      const newAssignments = [...teamAssignments]
                                      newAssignments[index] = {
                                        ...newAssignments[index],
                                        role: e.value
                                      }
                                      setTeamAssignments(newAssignments)
                                      setRoleChanged(true)
                                    }}
                                    placeholder="Select role"
                                    pt={DropdownStyle}
                                    className="w-full"
                                  />
                                </div>
                              ) : (
                                <div className="text-sm text-neutral-80 dark:text-neutral-10 mt-1">
                                  {role?.label || assignment.role}
                                </div>
                              )}
                            </div>
                            {editingEnabled && (
                              <div className="w-28 flex justify-end">
                                <Button
                                  type="button"
                                  icon="pi pi-trash"
                                  onClick={() => handleRemoveTeam(index)}
                                  className="p-button-danger p-button-sm p-button-text w-8 h-8"
                                  pt={{
                                    root: {
                                      className: 'w-8 h-8'
                                    },
                                    label: {
                                      className: 'hidden'
                                    }
                                  }}
                                />
                              </div>
                            )}
                          </div>

                          {/* Rescale Token Field */}
                          <div className="flex flex-col">
                            <label className="mb-1 text-sm font-semibold text-neutral-80 dark:text-white">
                              Rescale Token {editingEnabled && '(Optional)'}
                            </label>
                            {editingEnabled ? (
                              <InputText
                                value={assignment.rescaleToken || ''}
                                onChange={e => handleUpdateTeamToken(index, e.target.value)}
                                placeholder={
                                  assignment.hasToken
                                    ? 'Token on file — enter a new value to replace'
                                    : 'Enter rescale token'
                                }
                                className="w-full"
                              />
                            ) : (
                              <div className="text-sm text-neutral-70 dark:text-neutral-10">
                                {assignment.rescaleToken ? (
                                  <span className="font-mono">
                                    {assignment.rescaleToken.substring(0, 8)}••••••••
                                  </span>
                                ) : assignment.hasToken ? (
                                  <span className="font-mono">••••••••</span>
                                ) : (
                                  <span className="italic">No token assigned</span>
                                )}
                              </div>
                            )}
                          </div>

                          {assignment.isNew && (
                            <span className="inline-block px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                              New
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              {showAddTeam && (
                <div
                  ref={addTeamFormRef}
                  className="p-4 border border-blue-300 dark:border-blue-700 rounded bg-blue-50 dark:bg-blue-900/20">
                  <h4 className="font-semibold mb-3">Add Team Assignment</h4>
                  <div className="space-y-3">
                    <div className="flex flex-col">
                      <label className="mb-1 text-sm font-semibold text-neutral-80 dark:text-white">
                        Race Team *
                      </label>
                      <Dropdown
                        value={newTeamForm.raceTeamId}
                        options={raceTeamOptions}
                        onChange={e => setNewTeamForm({ ...newTeamForm, raceTeamId: e.value })}
                        placeholder="Select team"
                        filter
                        pt={DropdownStyle}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="mb-1 text-sm font-semibold text-neutral-80 dark:text-white">
                        Role *
                      </label>
                      <Dropdown
                        value={newTeamForm.role}
                        options={USER_ROLES}
                        onChange={e => setNewTeamForm({ ...newTeamForm, role: e.value })}
                        placeholder="Select role"
                        pt={DropdownStyle}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="mb-1 text-sm font-semibold text-neutral-80 dark:text-white">
                        Rescale Token (Optional)
                      </label>
                      <InputText
                        value={newTeamForm.rescaleToken}
                        onChange={e =>
                          setNewTeamForm({ ...newTeamForm, rescaleToken: e.target.value })
                        }
                        placeholder="Enter token"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button
                        type="button"
                        label="Cancel"
                        onClick={() => {
                          setShowAddTeam(false)
                          setNewTeamForm({ raceTeamId: '', role: '', rescaleToken: '' })
                        }}
                        className="p-button-outlined p-button-sm"
                      />
                      <Button
                        type="button"
                        label="Add"
                        onClick={handleAddTeam}
                        disabled={!editingEnabled}
                        className="p-button-sm"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div className="shrink-0 pt-4 border-t border-neutral-50 dark:border-neutral-80 flex justify-end gap-3 mt-4">
          <Button
            type="button"
            label="Cancel"
            onClick={handleCancel}
            className="btn bg-gray-500 text-white"
          />
          <Button
            type="button"
            label="Save Changes"
            onClick={handleSubmit(handleSave)}
            disabled={!editingEnabled || !hasChanges}
            loading={false}
            className="btn bg-blue-900 text-white"
          />
        </div>
      </div>
    </Modal>
  )
}

export default UserEditModal
